/**
 * TODO: use this file after moving to the new branding (https://app.clickup.com/t/86c0kfr9m)
 * Typography
 *
 * Master styles for headlines and text.
 * See: https://www.figma.com/file/iLh3LOpdfxU049JzeU10GU/Kittl-UI-Update-2023?node-id=5831%3A18331&t=nsgMdxlBvQIL2mSG-4
 *
 */

import { handleTextOverflow } from '@src/components/utilities';
import { QUERIES } from '@src/queries';
import { themeStyle } from '@src/theming';
import styled, { css, RuleSet } from 'styled-components';

import { HeadlineProps, ParagraphProps } from './types';

/**
 * Headline
 * padding: 0px;
 */
const h = css<HeadlineProps>`
  color: ${({ color }): string => color ?? themeStyle.varInkMain};

  padding: 0px;
  margin: ${({ margin }): string | number => margin || '0px'};

  ${({ noTextOverflow }): RuleSet | string =>
    noTextOverflow ? handleTextOverflow : ''};
`;

/**
 * @extends h
 * font-weight: 800;
 * font-size: 154px;
 * line-height: 128px;
 */
export const h1PlusLarge = css<HeadlineProps>`
  ${h};
  font-family: 'Druk';
  font-weight: ${themeStyle.fontBold};
  font-size: 154px;
  line-height: 128px;
  letter-spacing: 0;
  text-transform: uppercase;

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-size: 46px;
    line-height: 42px;
    letter-spacing: 0.01%;
  }

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontBold};
  }
`;

/**
 * @implements h1PlusLarge
 * @deprecated Use `H1PlusLarge` from `@repo/ui-kit` instead
 */
export const H1PlusLarge = styled.h1<HeadlineProps>`
  ${h1PlusLarge};
`;

/**
 * @extends h
 * font-weight: 800;
 * font-size: 100px;
 * line-height: 88px;
 */
export const h1PlusSmall = css<HeadlineProps>`
  ${h};
  font-family: 'Druk';
  font-weight: ${themeStyle.fontBold};
  font-size: 100px;
  line-height: 88px;
  letter-spacing: 0;
  text-transform: uppercase;

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-size: 38px;
    line-height: 36px;
    letter-spacing: 0.01%;
  }

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontBold};
  }
`;

/**
 * @implements h1PlusSmall
 * @deprecated Use `H1PlusSmall` from `@repo/ui-kit` instead
 */
export const H1PlusSmall = styled.h1<HeadlineProps>`
  ${h1PlusSmall};
`;

/**
 * @extends h
 * font-weight: 600;
 * font-size: 36px;
 * line-height: 42px;
 */
export const h1 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontSemiBold};
  font-size: 52px;
  line-height: 56px;
  letter-spacing: -0.04em; // -4% of the font-size

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-size: 28px;
    line-height: 32px;
    letter-spacing: -0.03em;
  }

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements h1
 * @deprecated Use `H1` from `@repo/ui-kit` instead
 */
export const H1 = styled.h1<HeadlineProps>`
  ${h1};
`;

/**
 * @extends h
 * font-weight: 600;
 * font-size: 32px;
 * line-height: 36px;
 */
export const h2 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontSemiBold};
  font-size: 32px;
  line-height: 36px;
  letter-spacing: -0.04em; // -4% of the font-size

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-size: 22px;
    line-height: 28px;
    letter-spacing: -0.03em; // -4% of the font-size
  }

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements h2
 * @deprecated Use `H2` from `@repo/ui-kit` instead
 */
export const H2 = styled.h2<HeadlineProps>`
  ${h2};
`;

/**
 * @extends h
 * font-weight: 500;
 * b, strong { font-weight: 600; }
 * font-size: 18px;
 * line-height: 24px;
 */
export const h3 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontMedium};
  font-size: 24px;
  line-height: 28px;
  letter-spacing: -0.03em; // -3% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }

  // Mobile
  ${QUERIES.largeTabletAndDown} {
    font-weight: ${themeStyle.fontSemiBold};
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.03em; // -3% of the font-size
  }
`;

/**
 * @implements h3
 * @deprecated Use `H3` from `@repo/ui-kit` instead
 */
export const H3 = styled.h3<HeadlineProps>`
  ${h3};
`;

/**
 * @extends h
 * font-weight: 500;
 * b, strong { font-weight: 600; }
 * font-size: 16px;
 * line-height: 20px;
 */
export const h4 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontMedium};
  font-size: 16px;
  line-height: 20px;
  letter-spacing: -0.015em; // -1.5% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements h4
 * @deprecated Use `H4` from `@repo/ui-kit` instead
 */
export const H4 = styled.h4<HeadlineProps>`
  ${h4};
`;

/**
 * @extends h
 * font-weight: 500;
 * b, strong { font-weight: 600; }
 * font-size: 14px;
 * line-height: 20px;
 */
export const h5 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.01em; // -1% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements h5
 * @deprecated Use `H5` from `@repo/ui-kit` instead
 */
export const H5 = styled.h5<HeadlineProps>`
  ${h5};
`;

/**
 * @extends h
 * font-weight: 500;
 * b, strong { font-weight: 600; }
 * font-size: 12px;
 * line-height: 16px;
 */
export const h6 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontMedium};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: -0.01em; // -1% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements h6
 * @deprecated Use `H6` from `@repo/ui-kit` instead
 */
export const H6 = styled.h6<HeadlineProps>`
  ${h6};
`;

/**
 * @extends h
 * font-weight: 700;
 * font-size: 12px;
 * line-height: 16px;
 */
export const h6Plus = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontBold};
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em; // 2% of the font-size
  text-transform: uppercase;

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontBold};
  }
`;

/**
 * @implements h6Plus
 * @deprecated Use `H6Plus` from `@repo/ui-kit` instead
 */
export const H6Plus = styled.h6<HeadlineProps>`
  ${h6Plus};
`;

/**
 * @extends h
 * font-weight: 700;
 * font-size: 11px;
 * line-height: 16px;
 */
export const h7 = css<HeadlineProps>`
  ${h};
  font-weight: ${themeStyle.fontMedium};
  font-size: 11px;
  line-height: 16px;
  letter-spacing: -0.005em; // -0.5% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements h7
 * @deprecated Use `H7` from `@repo/ui-kit` instead
 */
export const H7 = styled.h6<HeadlineProps>`
  ${h7};
`;

/**
 * Parapraph
 * > a { cursor: pointer; text-decoration-line: underline; }
 */
const p = css<ParagraphProps>`
  color: ${({ color }): string => color ?? themeStyle.varInkMain};

  padding: 0px;
  margin: ${({ margin }): string | number => margin || '0px'};
  text-decoration: ${({ textDecoration }): string => textDecoration ?? 'unset'};
  cursor: ${({ cursor }): string => cursor ?? 'unset'};

  & > a {
    cursor: pointer;
    color: ${({ color }): string => color ?? themeStyle.varInkMain};
    text-decoration-line: underline;
  }

  ${({ noTextOverflow }): RuleSet | string =>
    noTextOverflow ? handleTextOverflow : ''};
`;

/**
 * @extends p
 * font-weight: 400;
 * b, strong { font-weight: 600; }
 * font-size: 18px;
 * line-height: 28px;
 */
export const p1 = css<ParagraphProps>`
  ${p};
  font-weight: ${themeStyle.fontRegular};
  font-size: 18px;
  line-height: 28px;
  letter-spacing: -0.01em; // -1% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements p1
 * @deprecated Use `P1` from `@repo/ui-kit` instead
 */
export const P1 = styled.p<ParagraphProps>`
  ${p1};
`;

/**
 * @extends p
 * font-weight: 400;
 * b, strong { font-weight: 600; }
 * font-size: 16px;
 * line-height: 24px;
 */
export const p2 = css<ParagraphProps>`
  ${p};
  font-weight: ${themeStyle.fontRegular};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.01em; // -1% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements p2
 * @deprecated Use `P2` from `@repo/ui-kit` instead
 */
export const P2 = styled.p<ParagraphProps>`
  ${p2};
`;

/**
 * @extends p
 * font-weight: 500;
 * b, strong { font-weight: 600; }
 * font-size: 14px;
 * line-height: 20px;
 */
export const p3 = css<ParagraphProps>`
  ${p};
  font-weight: ${themeStyle.fontMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.005em; // -0.5% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements p3
 * @deprecated Use `P3` from `@repo/ui-kit` instead
 */
export const P3 = styled.p<ParagraphProps>`
  ${p3};
`;

/**
 * @extends p
 * font-weight: 500;
 * b, strong { font-weight: 600; }
 * font-size: 12px;
 * line-height: 20px;
 */
export const p4 = css<ParagraphProps>`
  ${p};
  font-weight: ${themeStyle.fontMedium};
  font-size: 12px;
  line-height: 20px;
  letter-spacing: -0.005em; // -0.5% of the font-size

  & > b,
  & > strong {
    font-weight: ${themeStyle.fontSemiBold};
  }
`;

/**
 * @implements p4
 * @deprecated Use `P4` from `@repo/ui-kit` instead
 */
export const P4 = styled.p<ParagraphProps>`
  ${p4};
`;
